import axios from "axios";
import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useRouter } from "next/router";
import { NextSeo } from "next-seo";
import Head from "next/head";
import Link from "next/link";
import { getIdToken } from "../utils/firebaseAuth";
import {
  Search,
  Flame,
  Layers,
  Mic,
  RotateCcw,
  ArrowRight,
  Play,
  Music2,
  Sparkles,
} from "lucide-react";
import { useAuth } from "@/context/AuthProvider";
import {
  SectionHeading,
  JamVoiceCard,
  JamCollectionCard,
  JamComboCard,
  JamCardSkeleton,
  JamVoiceCardSkeleton,
} from "../components/home/JamCards";
import type { HubCategory } from "@/utils/categoryHubs";

interface Trending {
  isTrending: boolean;
  position: number | undefined;
}

export interface ApiListResponse<T> {
  data: T[];
  paging: {
    hasMore: boolean;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  collections?: any[];
}

export interface Model {
  _id: string;
  artistname: string;
  uses: number;
  user: {
    username: string;
  };
  image: {
    url: string;
    blurhash?: string;
  };
  isPublic: boolean;
  urlParam: string;
  badges?: string[];
  accessIds: string[];
  likes?: number;
  trending: Trending;
  description?: string;
  createdAt: Date;
  updatedAt: Date;
}

interface HomePageProps {
  // Axios-response-shaped ({ data: <apiJson> }) seeds fetched server-side in
  // getStaticProps so the trending/collection grids — and their internal links
  // into the model long-tail — render in the initial HTML for crawlers. `null`
  // when the server-side fetch failed (page falls back to client fetch).
  trendingInitial?: unknown;
  topCollectionsInitial?: unknown;
  topCombosInitial?: unknown;
  collectionsInitial?: unknown;
  categories?: HubCategory[];
}

// The hero's floating pieces bob gently; the waveform plays.
const HERO_KEYFRAMES = `
@keyframes jam-hero-float { 0%,100% { translate: 0 0; } 50% { translate: 0 -10px; } }
@keyframes jam-hero-bar { 0%,100% { transform: scaleY(.45); } 50% { transform: scaleY(1); } }
.jam-hero-float { animation: jam-hero-float 5s ease-in-out infinite; }
.jam-hero-bar { animation: jam-hero-bar 1.1s ease-in-out infinite; transform-origin: center; }
@media (prefers-reduced-motion: reduce) { .jam-hero-float, .jam-hero-bar { animation: none; } }
`;

// Where the covers get watched, as small marks beside the views line.
const SOCIALS: { name: string; bg: string; mark: React.ReactNode }[] = [
  {
    name: "TikTok",
    bg: "#121212",
    mark: (
      <svg viewBox="0 0 24 24" width={14} height={14} fill="#fff" aria-hidden>
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
      </svg>
    ),
  },
  {
    name: "YouTube",
    bg: "#FF0033",
    mark: (
      <svg viewBox="0 0 24 24" width={13} height={13} fill="#fff" aria-hidden>
        <path d="M8 5.5v13l11-6.5z" />
      </svg>
    ),
  },
  {
    name: "Instagram",
    bg: "linear-gradient(45deg, #FEDA75, #FA7E1E 25%, #D62976 50%, #962FBF 75%, #4F5BD5)",
    mark: (
      <svg
        viewBox="0 0 24 24"
        width={15}
        height={15}
        fill="none"
        stroke="#fff"
        strokeWidth={2.4}
        aria-hidden
      >
        <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.3" cy="6.7" r="0.6" fill="#fff" />
      </svg>
    ),
  },
];

const HomePage = ({
  trendingInitial,
  topCollectionsInitial,
  topCombosInitial,
  collectionsInitial,
  categories = [],
}: HomePageProps) => {
  const router = useRouter();

  // Hero search — the primary action on the page.
  const [searchQ, setSearchQ] = useState("");
  const goToSearch = () => {
    const term = searchQ.trim();
    if (term) router.push(`/models?q=${encodeURIComponent(term)}`);
    else router.push("/models");
  };

  const { data: trendingVoices, isLoading: isTrendingLoading } = useQuery(
    "trending-voices",
    () => axios.get("/api/home/getTrendingVoices"),
    {
      initialData: (trendingInitial as never) ?? undefined,
      staleTime: 5 * 60 * 1000,
    },
  );

  const { data: topCollections, isLoading: isTopCollectionsLoading } = useQuery(
    "top-collections",
    () => axios.get("/api/home/getTopCollections"),
    {
      initialData: (topCollectionsInitial as never) ?? undefined,
      staleTime: 5 * 60 * 1000,
    },
  );

  const { data: topCombos, isLoading: isTopCombosLoading } = useQuery(
    "top-combos",
    () => axios.get("/api/home/getTopCombos"),
    {
      initialData: (topCombosInitial as never) ?? undefined,
      staleTime: 5 * 60 * 1000,
    },
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { user, loading: authLoading } = useAuth() as any;
  // Firebase restores the session a moment after the page shows; the auth
  // cookie (AuthProvider sets it, empty when signed out) says sooner whether
  // there will be a "Jump back in" row, so its place can be held at once.
  const [signedInHint, setSignedInHint] = useState(false);
  useEffect(() => {
    setSignedInHint(/(?:^|;\s*)token=[^;]+/.test(document.cookie));
  }, []);
  const { data: recentVoices, isLoading: isRecentVoicesLoading } = useQuery(
    ["recent-voices", user?.uid],
    async () => {
      const token = await getIdToken();
      return axios.get("/api/home/getRecentVoices", {
        headers: {
          Authorization: token,
        },
      });
    },
    { enabled: !!user },
  );

  const { data: collections } = useQuery(
    "collections",
    () => axios.get("/api/home/getCollections"),
    {
      initialData: (collectionsInitial as never) ?? undefined,
      staleTime: 5 * 60 * 1000,
    },
  );


  const titleCase = (s: string) =>
    s
      .split(" ")
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
      .join(" ");

  const trending: Model[] = trendingVoices?.data?.models || [];
  const recent: Model[] = recentVoices?.data?.voices || [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const topCols: any[] = topCollections?.data?.collections || [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const combos: any[] = topCombos?.data?.models || [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const moreCols: any[] = (collections?.data?.collections || []).filter(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (c: any) => c.modelDetails?.length > 0,
  );

  return (
    <div className="tw-bg-white tw-text-jam-ink">
      <NextSeo
        title={"Jammable | Create AI Covers with your Favorite Voices!"}
        description={
          "Create AI covers in seconds with Jammable, with thousands of community uploaded AI voice models available for creative use now!"
        }
        canonical="https://www.jammable.com"
        themeColor="#782DC8"
        openGraph={{
          title: `Jammable | Create AI Covers with Your Favorite Voices | AI-Powered Music Creation`,
          description:
            "Create AI covers in seconds with Jammable, with thousands of community uploaded AI voice models available for creative use now!",
          url: "https://www.jammable.com",
          type: "website",
          locale: "en_US",
          siteName: "Jammable",
          images: [
            {
              url: "https://www.jammable.com/android-chrome-512x512.png",
              width: 512,
              height: 512,
              alt: "Jammable - Create AI Covers",
            },
          ],
        }}
        twitter={{
          handle: "@jammable",
          site: "@jammable",
          cardType: "summary_large_image",
        }}
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: [
                {
                  "@type": "Question",
                  name: "What is Jammable?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Jammable is the #1 AI cover platform that lets you create song covers using thousands of community-created AI voice models. Upload audio you have the rights to use and hear it performed in a different voice. Formerly known as Voicify AI.",
                  },
                },
                {
                  "@type": "Question",
                  name: "How do AI covers work on Jammable?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Jammable uses AI voice models to transform songs. You upload or paste a song link, choose a voice from the library (or train your own), and the AI re-synthesizes the vocals in that voice while keeping the original instrumental. The process takes seconds.",
                  },
                },
                {
                  "@type": "Question",
                  name: "What voices are available on Jammable?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Jammable has thousands of community-created AI voice models across categories including anime characters, cartoon characters, gaming personalities, K-pop voices, and more — available in multiple languages including Korean, German, French, and Italian. Users can also train custom voice models.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Can I train my own AI voice model on Jammable?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Yes. With the Creator plan, you can train unlimited custom voice models by uploading audio samples. This lets you create AI covers using any voice — your own, a friend's, or any unique voice you want to replicate.",
                  },
                },
                {
                  "@type": "Question",
                  name: "What is the difference between Jammable and Voicify AI?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Jammable is the rebranded version of Voicify AI. It's the same platform with the same team, but with an updated name and expanded features including duets, text-to-speech, video creation, and a larger voice library.",
                  },
                },
              ],
            }),
          }}
        />
      </Head>

      <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-[1280px] tw-flex-col tw-gap-12 tw-px-4 tw-pt-2 md:tw-gap-14 md:tw-px-8">
        {/* Hero: search is the primary action, Make a cover the second. */}
        <section className="tw-relative tw-flex tw-flex-col tw-items-center tw-gap-5 tw-overflow-hidden tw-rounded-[34px] tw-bg-jam-lav tw-px-5 tw-pb-8 tw-pt-10 tw-text-center md:tw-rounded-[44px] md:tw-px-12 md:tw-pb-12 md:tw-pt-16">
          {/* Desktop decoration: what Jammable does, never a specific voice. */}
          <div className="tw-pointer-events-none tw-hidden lg:tw-block" aria-hidden>
            <style>{HERO_KEYFRAMES}</style>
            {/* A cover playing */}
            <div
              className="jam-hero-float tw-absolute tw-left-10 tw-top-[96px] tw-flex tw-items-center tw-gap-3 tw-rounded-[22px] tw-bg-white tw-p-3 tw-pr-4 tw-shadow-[0_18px_40px_-20px_rgba(46,15,82,0.35)]"
              style={{ rotate: "-6deg", animationDelay: "0s" }}
            >
              <span className="tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-purple tw-text-white">
                <Play size={18} strokeWidth={0} fill="currentColor" className="tw-ml-0.5" />
              </span>
              <span className="tw-flex tw-h-8 tw-items-center tw-gap-[3px]">
                {[0.5, 0.9, 0.6, 1, 0.7, 0.4, 0.85, 0.55, 0.75, 0.45].map((h, i) => (
                  <i
                    key={i}
                    className="jam-hero-bar tw-block tw-w-[4px] tw-rounded-full tw-bg-jam-purple"
                    style={{ height: `${h * 100}%`, animationDelay: `${i * 0.11}s` }}
                  />
                ))}
              </span>
            </div>
            {/* A mic */}
            <span
              className="jam-hero-float tw-absolute tw-left-[120px] tw-top-[300px] tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lime tw-text-jam-ink tw-shadow-[0_14px_30px_-16px_rgba(46,15,82,0.4)]"
              style={{ rotate: "10deg", animationDelay: "1.2s" }}
            >
              <Mic size={26} strokeWidth={2.5} />
            </span>
            {/* Voice in, cover out */}
            <span
              className="jam-hero-float tw-absolute tw-right-10 tw-top-[110px] tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-jam-ink tw-px-4 tw-py-2.5 tw-text-[13px] tw-font-extrabold tw-text-white tw-shadow-[0_18px_40px_-20px_rgba(46,15,82,0.5)]"
              style={{ rotate: "5deg", animationDelay: "0.6s" }}
            >
              <Sparkles size={15} strokeWidth={2.5} className="tw-text-jam-lime" />
              AI voice
              <ArrowRight size={14} strokeWidth={3} className="tw-text-jam-soft" />
              cover
            </span>
            {/* A note */}
            <span
              className="jam-hero-float tw-absolute tw-right-[130px] tw-top-[290px] tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-[18px] tw-bg-jam-purple tw-text-white tw-shadow-[0_14px_30px_-16px_rgba(46,15,82,0.5)]"
              style={{ rotate: "-8deg", animationDelay: "1.8s" }}
            >
              <Music2 size={24} strokeWidth={2.5} />
            </span>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2.5 tw-rounded-full tw-bg-white tw-py-1.5 tw-pl-1.5 tw-pr-4">
            <div className="tw-flex">
              {SOCIALS.map(({ name, bg, mark }, i) => (
                <span
                  key={name}
                  title={name}
                  style={{ background: bg, marginLeft: i ? -8 : 0 }}
                  className="tw-flex tw-h-[30px] tw-w-[30px] tw-items-center tw-justify-center tw-rounded-full tw-border-2 tw-border-solid tw-border-white"
                >
                  {mark}
                </span>
              ))}
            </div>
            <span className="tw-text-xs tw-font-extrabold md:tw-text-sm">
              10 billion+ views on TikTok, YouTube and Instagram
            </span>
          </div>

          <h1 className="jam-display tw-m-0 tw-max-w-[900px] tw-text-[40px] tw-font-extrabold tw-leading-[1.02] md:tw-text-[76px] md:tw-leading-none">
            Create{" "}
            <span className="tw-whitespace-nowrap tw-rounded-[14px] tw-bg-jam-lime tw-px-2 tw-pb-0.5 md:tw-rounded-[22px] md:tw-px-4 md:tw-pb-1">
              AI covers
            </span>{" "}
            with your favourite voices
          </h1>
          <p className="tw-m-0 tw-text-[15px] tw-font-semibold tw-text-jam-muted md:tw-text-[19px]">
            Pick from 100,000+ AI voices, add your audio, and get it back in
            seconds.
          </p>

          <div className="tw-mt-1 tw-flex tw-w-full tw-max-w-[760px] tw-flex-col tw-gap-2.5 md:tw-flex-row">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                goToSearch();
              }}
              className="tw-flex tw-flex-grow tw-items-center tw-gap-3 tw-rounded-full tw-bg-white tw-py-2 tw-pl-6 tw-pr-2"
            >
              <Search size={20} strokeWidth={2.5} className="tw-text-jam-muted" />
              <label htmlFor="home-search" className="tw-sr-only">
                Search voices
              </label>
              <input
                id="home-search"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search 100,000+ voices"
                className="tw-min-w-0 tw-flex-grow tw-border-0 tw-bg-transparent tw-text-base tw-font-bold tw-text-jam-ink tw-outline-none md:tw-text-[17px]"
              />
              <button
                type="submit"
                className="tw-h-12 tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-jam-purple tw-px-6 tw-text-[15px] tw-font-extrabold tw-text-white md:tw-h-[52px]"
              >
                Search
              </button>
            </form>
            <Link
              href="/create"
              className="tw-flex tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-full tw-bg-jam-ink tw-px-7 tw-py-4 tw-text-[15px] tw-font-extrabold tw-text-white tw-no-underline"
            >
              Make a cover
            </Link>
          </div>

          <div className="tw-hidden tw-flex-wrap tw-justify-center tw-gap-2 md:tw-flex">
            {[
              { label: "AI Cover Generator", href: "/ai-cover" },
              { label: "AI Voice Changer", href: "/ai-voice-changer" },
              { label: "Train a voice", href: "/train" },
            ].map((l) => (
              <Link
                key={l.href}
                href={l.href}
                className="tw-rounded-full tw-bg-white tw-px-[18px] tw-py-2.5 tw-text-sm tw-font-extrabold tw-text-jam-ink tw-no-underline"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </section>

        {/* Categories: server-rendered links into the hub pages (SEO). */}
        {categories.length > 0 && (
          <section className="tw--mt-6 tw-flex tw-gap-2 tw-overflow-x-auto tw-pb-1 md:tw-flex-wrap md:tw-overflow-visible [&::-webkit-scrollbar]:tw-hidden">
            {categories.map((c) => (
              <Link
                key={c.slug}
                href={`/ai-voices/${c.slug}`}
                className="tw-flex-shrink-0 tw-whitespace-nowrap tw-rounded-full tw-border-2 tw-border-solid tw-border-jam-line tw-px-4 tw-py-2 tw-text-sm tw-font-extrabold tw-text-jam-ink tw-no-underline hover:tw-border-jam-purple"
              >
                {titleCase(c.badge)}
              </Link>
            ))}
            <Link
              href="/models"
              className="tw-flex-shrink-0 tw-whitespace-nowrap tw-px-4 tw-py-2.5 tw-text-sm tw-font-extrabold tw-text-jam-purple tw-no-underline"
            >
              See all categories
            </Link>
          </section>
        )}

        {/* Signed in: the row holds its place (heading and placeholder
          pills) while the recent voices load, so it doesn't arrive late and
          push the page down. */}
        {(recent.length > 0 ||
          ((!!user || (signedInHint && authLoading)) &&
            (isRecentVoicesLoading || !recentVoices))) && (
          <section className="tw-flex tw-flex-col tw-gap-4">
            <SectionHeading
              title="Jump back in"
              icon={<RotateCcw size={17} strokeWidth={2.6} className="tw-text-jam-purple" />}
            />
            <div className="tw-flex tw-gap-2.5 tw-overflow-x-auto [&::-webkit-scrollbar]:tw-hidden">
              {recent.length === 0 &&
                [128, 156, 112, 144, 120].map((w, i) => (
                  <span
                    key={i}
                    aria-hidden
                    className="tw-h-12 tw-flex-shrink-0 tw-animate-pulse tw-rounded-full tw-bg-jam-lav"
                    style={{ width: w }}
                  />
                ))}
              {recent.map((model) => (
                <Link
                  key={model._id}
                  href={`/${model.urlParam}`}
                  className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2 tw-rounded-full tw-bg-jam-lav tw-py-1.5 tw-pl-1.5 tw-pr-4 tw-text-sm tw-font-extrabold tw-text-jam-ink tw-no-underline"
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={model.image?.url}
                    alt=""
                    className="tw-h-9 tw-w-9 tw-rounded-full tw-object-cover"
                  />
                  {model.artistname}
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="tw-flex tw-flex-col tw-gap-4">
          <SectionHeading
            title="Trending now"
            iconBg="tw-bg-[#FFE3CC]"
            icon={<Flame size={18} strokeWidth={2.6} className="tw-text-[#F07A1A]" />}
            aside={
              <Link
                href="/models"
                className="tw-text-sm tw-font-extrabold tw-text-jam-purple tw-no-underline"
              >
                See all
              </Link>
            }
          />
          <div className="tw-grid tw-grid-cols-3 tw-gap-2.5 md:tw-grid-cols-6 md:tw-gap-3.5">
            {isTrendingLoading
              ? [...Array(12)].map((_, i) => (
                  <JamVoiceCardSkeleton key={i} />
                ))
              : trending.slice(0, 12).map((model, i) => (
                  <JamVoiceCard key={model._id} model={model} rank={i + 1} />
                ))}
          </div>
        </section>

        <section className="tw-grid tw-gap-6 tw-rounded-[34px] tw-bg-jam-ink tw-p-6 tw-text-white md:tw-grid-cols-[1.1fr_1fr] md:tw-gap-10 md:tw-rounded-[44px] md:tw-p-12">
          <div className="tw-flex tw-flex-col tw-gap-4">
            <span className="tw-text-sm tw-font-extrabold tw-text-jam-soft">
              100,000+ voices started this way
            </span>
            <h2 className="jam-display tw-m-0 tw-text-[34px] tw-font-extrabold tw-leading-none md:tw-text-[54px]">
              Train your own{" "}
              <span className="tw-whitespace-nowrap tw-rounded-[14px] tw-bg-jam-lime tw-px-2.5 tw-pb-0.5 tw-text-jam-ink md:tw-rounded-[18px]">
                AI voice
              </span>
            </h2>
            <p className="tw-m-0 tw-max-w-[460px] tw-text-[15px] tw-font-semibold tw-text-[#E3D3F7] md:tw-text-[17px]">
              Build a voice from a few samples, then make covers in a voice
              nobody else has.
            </p>
            <Link
              href="/train"
              className="tw-self-start tw-rounded-full tw-bg-jam-lime tw-px-7 tw-py-4 tw-text-base tw-font-extrabold tw-text-jam-ink tw-no-underline"
            >
              Train a voice
            </Link>
          </div>
          <div className="tw-hidden tw-flex-col tw-gap-2.5 md:tw-flex">
            {[
              ["Upload your samples", "A short clip of clear vocals is all you need"],
              ["We train your model", "Your custom voice is ready in minutes"],
              ["Make covers with it", "Use it in your covers, whenever you want"],
            ].map(([title, sub], i) => (
              <div
                key={title}
                className="tw-flex tw-items-center tw-gap-3.5 tw-rounded-[26px] tw-bg-jam-deep tw-p-[18px]"
              >
                <span
                  className={`tw-flex tw-h-11 tw-w-11 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-font-extrabold ${
                    i === 2 ? "tw-bg-jam-lime tw-text-jam-ink" : "tw-bg-jam-purple"
                  }`}
                >
                  {i + 1}
                </span>
                <div>
                  <div className="tw-text-base tw-font-extrabold">{title}</div>
                  <div className="tw-text-[13px] tw-font-semibold tw-text-jam-soft">{sub}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="tw-flex tw-flex-col tw-gap-4">
          <SectionHeading
            title="Top collections"
            iconBg="tw-bg-[#EEFFB0]"
            icon={<Layers size={18} strokeWidth={2.6} className="tw-text-[#4F6B00]" />}
          />
          <div className="tw-flex tw-gap-3.5 tw-overflow-x-auto md:tw-grid md:tw-grid-cols-5 md:tw-overflow-visible [&::-webkit-scrollbar]:tw-hidden">
            {isTopCollectionsLoading
              ? [...Array(5)].map((_, i) => (
                  <JamCardSkeleton key={i} className="tw-h-[210px] tw-min-w-[160px]" />
                ))
              : topCols.slice(0, 5).map((collection) => (
                  <div key={collection._id} className="tw-min-w-[160px]">
                    <JamCollectionCard collection={collection} />
                  </div>
                ))}
          </div>
        </section>

        {combos.length > 0 && (
          <section className="tw-flex tw-flex-col tw-gap-4">
            <SectionHeading
              title="Trending collabs"
              icon={<Mic size={18} strokeWidth={2.6} className="tw-text-jam-purple" />}
            />
            <div className="tw-flex tw-gap-3.5 tw-overflow-x-auto md:tw-grid md:tw-grid-cols-4 md:tw-overflow-visible [&::-webkit-scrollbar]:tw-hidden">
              {combos.slice(0, isTopCombosLoading ? 0 : 8).map((combo) => (
                <div key={String(combo._id)} className="tw-min-w-[220px]">
                  <JamComboCard combo={combo} />
                </div>
              ))}
            </div>
          </section>
        )}

        {moreCols.map((collection) => (
          <section key={collection._id} className="tw-flex tw-flex-col tw-gap-4">
            <SectionHeading
              title={collection.title}
              aside={
                <Link
                  href={`/collection/${collection._id}`}
                  className="tw-flex tw-items-center tw-gap-1 tw-whitespace-nowrap tw-text-sm tw-font-extrabold tw-text-jam-purple tw-no-underline"
                >
                  Collection <ArrowRight size={16} strokeWidth={2.6} />
                </Link>
              }
            />
            <div className="tw-grid tw-grid-cols-3 tw-gap-2.5 md:tw-grid-cols-6 md:tw-gap-3.5">
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {collection.modelDetails.slice(0, 6).map((model: any) => (
                <JamVoiceCard key={model._id} model={model} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default HomePage;


// Render the home page as ISR (static + revalidation) and seed the public
// grids + category directory server-side so crawlers get real content and
// internal links to the 90k+ model long-tail and category hubs — instead of an
// empty client-rendered shell. The per-user "recent voices" rail stays
// client-only. Any failure degrades gracefully to client-fetch behavior.
export async function getStaticProps() {
  const base = "https://www.jammable.com";

  const fetchInitial = async (path: string) => {
    try {
      const res = await axios.get(`${base}${path}`, { timeout: 4000 });
      return { data: res.data };
    } catch {
      return null;
    }
  };

  let categories: HubCategory[] = [];
  try {
    const { getHubCategories } = await import("@/utils/categoryHubs");
    // Not featured on the home page: Public Figure.
    categories = (await getHubCategories(50))
      .filter((c) => c.slug !== "public-figure" && c.badge.toLowerCase() !== "public figure")
      .slice(0, 16);
  } catch {
    categories = [];
  }

  const [
    trendingInitial,
    topCollectionsInitial,
    topCombosInitial,
    collectionsInitial,
  ] = await Promise.all([
    fetchInitial("/api/home/getTrendingVoices"),
    fetchInitial("/api/home/getTopCollections"),
    fetchInitial("/api/home/getTopCombos"),
    fetchInitial("/api/home/getCollections"),
  ]);

  // Seed only what the first render shows (trending 12, top collections 5,
  // combos 8). The APIs return 30/30/24, and serialising all of it made
  // __NEXT_DATA__ 170KB of the 434KB home HTML; trimmed it is ~66KB.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const trim = (r: any, key: string, n: number) =>
    r?.data?.[key]
      ? { data: { ...r.data, [key]: r.data[key].slice(0, n) } }
      : r;

  return {
    props: {
      trendingInitial: trim(trendingInitial, "models", 12),
      topCollectionsInitial: trim(topCollectionsInitial, "collections", 5),
      topCombosInitial: trim(topCombosInitial, "models", 8),
      collectionsInitial,
      categories,
    },
    revalidate: 600,
  };
}
